import {Inject, Injectable} from '@angular/core';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {BooleanUtils, ColDefBuilderFactoryService, LinkIconRendererComponent, StringUtils, TranslateService} from '@vdw/angular-component-library';
import {CellClassParams, ColDef, ICellRendererParams, ITooltipParams} from 'ag-grid-community';
import {OverviewWeaveQualityTypeComponent} from '../../machine-quality/overview-weave-quality-type/overview-weave-quality-type.component';
import {GridOwnerLabelComponent} from './grid-owner-label/grid-owner-label.component';
import {OverviewListWeaveStructure} from './overview-list-weave-structure';

@Injectable()
export class OverviewListWeaveStructureService {
  private pageUrls = LastModifiedCardUtils.getPageUrls('weaveStructure');

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService
  ) {}

  public getColumnDefs(canClickName: boolean, isManagedRoute: boolean, showQualityType: boolean): ColDef[] {
    const columnDefs = [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          return {
            params,
            canClick: canClickName && !isManagedRoute,
            editPageUrl: this.pageUrls.edit
          };
        })
        .withCellClass((cellClassParams: CellClassParams) => {
          const result = [];

          const weaveStructure: OverviewListWeaveStructure = cellClassParams.data;
          if (weaveStructure.name.length === 0) {
            result.push('object-without-name');
          }
          return result;
        })
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('version', true).withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.VERSION').withLockVisible().build(),
      this.colDefBuilderFactoryService.getBuilder().withField('type', true).withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.TYPE').build(),
      this.colDefBuilderFactoryService.getBuilder().withField('description', true).withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.DESCRIPTION').build(),
      this.colDefBuilderFactoryService.getBuilder().withField('releaseNotes', true).withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.RELEASE_NOTES').build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('fromVandewiele')
        .withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.OWNER')
        .withCellRenderer(GridOwnerLabelComponent)
        .withComparator(BooleanUtils.booleanComparator)
        .withPinned(false)
        .withoutFilter()
        .withSortable()
        .withTooltipValueGetter((params: ITooltipParams) => (params.data.fromVandewiele ? 'Vandewiele' : this.authentication.getCurrentSubscription().name).toUpperCase())
        .build()
    ];

    if (isManagedRoute) {
      columnDefs.splice(4, 0, this.colDefBuilderFactoryService.getBuilder().withField('fileName', true).withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.FILE_NAME').build());
    } else {
      columnDefs.push(
        this.colDefBuilderFactoryService.getBuilder().withField('dateModified').withHeaderName('GENERAL.DATETIME.DATE').withDateMultiFilter().withDate().build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('picksPerColorLine', true)
          .withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_SET_SIZE_Y')
          .withNumericMultiFilter()
          .withHide(true)
          .build()
      );
    }

    if (showQualityType) {
      columnDefs.splice(
        3,
        0,
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('qualityType')
          .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.QUALITY_TYPE')
          .withCellRenderer(OverviewWeaveQualityTypeComponent)
          .withComparator(StringUtils.stringComparator)
          .withTooltipValueGetter((params: ITooltipParams) => this.translate.instant(`TEXTILE_DATA.MACHINE_QUALITY.TYPES.${params.data.qualityType}`))
          .build()
      );
    }

    return columnDefs;
  }
}
